import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
} from "@common/types/auditLog";
import {
    getHistoryFieldLabel,
    isRecord,
} from "../../utils/historyDetailsHelpers";
import {
    HistoryClassCreateDeleteDetailsRow,
    HistoryClassUpdateDetailsRow,
} from "../views/historyClassDetailsViewConfig";

function formatAuditLogRefValue(
    ref: AuditLogDiff["before"] | AuditLogDiff["after"],
): string {
    if (ref.valueLabel) {
        return ref.valueLabel;
    }
    const value = ref.value ?? null;
    if (value === null || value === undefined) {
        return "-";
    }
    if (typeof value === "boolean") {
        return value ? "yes" : "no";
    }
    if (typeof value === "string" || typeof value === "number") {
        return String(value);
    }
    return JSON.stringify(value);
}

function getClassNameFromMetadata(log: HistoryDetailsItem): string | undefined {
    if (!isRecord(log.metadata)) {
        return undefined;
    }

    const before = isRecord(log.metadata.beforeSnapshot)
        ? log.metadata.beforeSnapshot
        : undefined;
    const after = isRecord(log.metadata.afterSnapshot)
        ? log.metadata.afterSnapshot
        : undefined;
    const preferredSnapshot =
        log.changeType === AuditLogChangeType.DELETE
            ? (before ?? after)
            : (after ?? before);
    const snapshotLabel = preferredSnapshot?.label;

    if (typeof snapshotLabel === "string" && snapshotLabel.length > 0) {
        return snapshotLabel;
    }

    return undefined;
}

function getClassNameFromDiff(log: HistoryDetailsItem): string | undefined {
    const labelDiff = log.diff.find((diff) => diff.field === "label");
    if (!labelDiff) {
        return undefined;
    }

    const preferredRef =
        log.changeType === AuditLogChangeType.DELETE
            ? labelDiff.before
            : labelDiff.after;
    const value = formatAuditLogRefValue(preferredRef);

    if (value === "-") {
        return undefined;
    }

    return value;
}

export function resolveClassDisplayName(log: HistoryDetailsItem): string {
    const resourceLabel = log.resource?.label;
    if (typeof resourceLabel === "string" && resourceLabel.length > 0) {
        return resourceLabel;
    }

    const metadataLabel = getClassNameFromMetadata(log);
    if (metadataLabel) {
        return metadataLabel;
    }

    const diffLabel = getClassNameFromDiff(log);
    if (diffLabel) {
        return diffLabel;
    }

    return "Class";
}

export function buildClassCreateDeleteDetailsRow(
    log: HistoryDetailsItem,
): HistoryClassCreateDeleteDetailsRow {
    const className = resolveClassDisplayName(log);
    const isDelete = log.changeType === AuditLogChangeType.DELETE;

    return {
        id: log.id,
        resourceType: log.resourceType,
        resourceId: log.resourceId,
        className,
        previous: isDelete ? className : "-",
        modified: isDelete ? "-" : className,
        logDate: log.createdAt,
        date: log.createdAt,
        entityId: log.entityId ?? null,
    };
}

export function buildClassUpdateDetailsRow(
    log: HistoryDetailsItem,
): HistoryClassUpdateDetailsRow {
    const className = resolveClassDisplayName(log);
    const base = {
        id: log.id,
        resourceType: log.resourceType,
        resourceId: log.resourceId,
        className,
        logDate: log.createdAt,
        date: log.createdAt,
        entityId: log.entityId ?? null,
    };

    if (
        log.changeType === AuditLogChangeType.CREATE ||
        log.changeType === AuditLogChangeType.DELETE
    ) {
        const createDeleteRow = buildClassCreateDeleteDetailsRow(log);
        return {
            ...base,
            changedFieldLabel: null,
            previous: createDeleteRow.previous,
            modified: createDeleteRow.modified,
        };
    }

    const uniqueFields = Array.from(
        new Set(log.diff.map((diff) => diff.field)),
    );
    if (uniqueFields.length === 1) {
        const [field] = uniqueFields;
        const diff = log.diff[0];
        return {
            ...base,
            changedFieldLabel: getHistoryFieldLabel(field),
            previous: formatAuditLogRefValue(diff.before),
            modified: formatAuditLogRefValue(diff.after),
        };
    }

    return {
        ...base,
        changedFieldLabel: null,
        previous: log.diff
            .map((diff) => {
                const fieldLabel = getHistoryFieldLabel(diff.field);
                return `${fieldLabel}: ${formatAuditLogRefValue(diff.before)}`;
            })
            .join(" · "),
        modified: log.diff
            .map((diff) => {
                const fieldLabel = getHistoryFieldLabel(diff.field);
                return `${fieldLabel}: ${formatAuditLogRefValue(diff.after)}`;
            })
            .join(" · "),
    };
}
